import React, { useState } from 'react';
import {
  Box, Button, Card, CardContent, Typography,
  Chip, Stack, IconButton, Tooltip,
} from '@mui/material';
import { PersonSearch, Edit, CreditCard, LocalOffer } from '@mui/icons-material';
import ClienteSearch from '../../../components/Tesoreria/ClienteSearch';
import { POSCliente } from './types';
import { formatCurrency } from '../../../utils/formatters';

interface Props {
  cliente: POSCliente | null;
  onClienteSelect: (cliente: POSCliente) => void;
  readOnly?: boolean;
}

const TIPO_IVA_LABELS: Record<string, string> = {
  RI: 'Resp. Inscripto',
  CF: 'Consumidor Final',
  EX: 'Exento',
  MO: 'Monotributista',
  NI: 'No Inscripto',
};

const FacturaPOSClientePanel: React.FC<Props> = ({ cliente, onClienteSelect, readOnly = false }) => {
  const [searchOpen, setSearchOpen] = useState(false);

  const handleSelect = (c: any) => {
    onClienteSelect(c as POSCliente);
    setSearchOpen(false);
  };

  const nroListaLabel =
    cliente?.listaPrecio?.descripcion
    ?? cliente?.listaPrecio?.codigoLista
    ?? cliente?.nroLista;

  return (
    <Box>
      <ClienteSearch
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onSelect={handleSelect}
      />

      {!cliente ? (
        <Button
          variant="outlined"
          fullWidth
          startIcon={<PersonSearch />}
          onClick={() => !readOnly && setSearchOpen(true)}
          disabled={readOnly}
          sx={{ py: 1.5, borderStyle: 'dashed', borderRadius: 2, textTransform: 'none' }}
        >
          Seleccionar cliente
        </Button>
      ) : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <Box flex={1} minWidth={0}>
                <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                  <Typography variant="subtitle1" fontWeight={600} noWrap>
                    {cliente.razonSocial || cliente.nombre}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    #{cliente.nroCliente}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {cliente.cuit && (
                    <Typography variant="body2" color="text.secondary">
                      CUIT: {cliente.cuit}
                    </Typography>
                  )}
                  {cliente.tipoIva && (
                    <Chip
                      label={TIPO_IVA_LABELS[cliente.tipoIva] ?? cliente.tipoIva}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                  {nroListaLabel && (
                    <Chip
                      icon={<LocalOffer sx={{ fontSize: '12px !important' }} />}
                      label={`Lista: ${nroListaLabel}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                  {cliente.condVta && (
                    <Chip
                      label={cliente.condVta}
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'action.hover' }}
                    />
                  )}
                </Stack>

                {cliente.credito != null && cliente.credito > 0 && (
                  <Stack direction="row" alignItems="center" spacing={0.5} mt={0.75}>
                    <CreditCard sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">Crédito:</Typography>
                    <Typography variant="caption" fontWeight={600} color="success.main">
                      {formatCurrency(cliente.credito)}
                    </Typography>
                  </Stack>
                )}

                {cliente.vendedorInfo?.nombreVendedor && (
                  <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                    Vendedor: {cliente.vendedorInfo.nombreVendedor}
                  </Typography>
                )}
              </Box>

              {!readOnly && (
                <Tooltip title="Cambiar cliente">
                  <IconButton size="small" onClick={() => setSearchOpen(true)}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default FacturaPOSClientePanel;
